import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { groq } from 'next-sanity';
import React from 'react';
import { Category } from '.';
import GrayLink from '../../../components/GrayLink';
import ArticlesByCategory from '../../../components/learn/articles/ArticlesByCategory';
import SupportArticlesButton from '../../../components/learn/SupportArticlesButton';
import { getClient } from '../../../lib/sanity.server';

export interface Article {
  title: string;
  slug: string;
  body: string;
  coverVideoUrl?: string;
  coverImageAsset?: string;
  category: string;
}

interface Props {
  articles: Array<Article>;
  category: Category;
}

const ArticleCategories: NextPage<Props> = ({ category, articles }) => {
  return (
    <div className='max-w-6xl mx-auto mb-32'>
      <div className='mt-32 body-paragraph text-gray-50'>
        <GrayLink link='/learn' text='Learn' /> /&nbsp;
        <GrayLink link='/learn/articles' text='Online Ministry Resources' />
      </div>
      <h1 className='mt-2'>{category.title}</h1>
      <ArticlesByCategory
        key={category.slug}
        category={category}
        articles={articles}
        isLandingPage={false}
      />
      <div className='mt-24 mb-24'>
        <SupportArticlesButton />
      </div>
    </div>
  );
};

export const getStaticPaths: GetStaticPaths<{ slug: string }> = async () => {
  const paths = await getClient().fetch<Array<string>>(groq`
  *[_type == 'articleCategory' && defined(slug.current)][].slug.current
  `);

  return {
    paths: paths.map(slug => ({ params: { slug } })),
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<{
  category: Category;
  articles: Array<Article>;
}> = async ({ params, preview = false }) => {
  const category = await getClient(preview).fetch<Category>(
    groq`
    *[_type=="articleCategory" && slug.current==$slug][0] {
      "slug": slug.current,
      title,
      description,
    }
  `,
    {
      slug: params?.slug,
    }
  );

  if (!category) {
    return {
      notFound: true,
    };
  }

  const articles = await getClient(preview).fetch<Array<Article>>(
    groq`
    *[_type=="article" && category->.slug.current==$slug] {
      "slug": slug.current,
      "coverVideoUrl": media[0]->.assetId,
      "coverImageAsset": media[0].asset,
      title,
      "category": category->.slug.current
    }
  `,
    {
      slug: params?.slug,
    }
  );

  const articlesWithVideos = await Promise.all(
    articles.map(async article => {
      if (article.coverVideoUrl) {
        const response = await fetch(
          `http://fast.wistia.net/oembed?url=http://home.wistia.com/medias/${article.coverVideoUrl}?embedType=async&videoWidth=640`,
          {
            method: 'GET',
          }
        );

        const data = await response.json();
        article.coverVideoUrl = data.thumbnail_url;
      }
      return article;
    })
  );

  return {
    props: {
      category,
      articles: articlesWithVideos,
    },
  };
};

export default ArticleCategories;
